import { AnalyticsEvent, AnalyticsManager } from '@oy/analytics';
import { Colors } from '@oy/uicore';
import { ButtonWithIcon, Loader } from '@oy/uidesktop';
import { SystemIcon } from '@oy/uiicon';
import classNames from 'classnames';
import { first } from 'lodash-es';
import PropTypes from 'prop-types';
import { Component } from 'react';
import ReactTable from 'react-table';
import { EmptyState, RecordsLimiter } from 'src/components';
import { EXPORT_OPTIONS } from 'src/constants';
import { getLanguage, getTranslation } from 'src/utilities/translator';
import DoubleScrollbar from './DoubleScrollbar';
import { ExportReportsModal } from './ExportReportsModal';
import styles from './RecordsTable.module.css';
import RecordsTableHeader from './RecordsTableHeader';

const prefixKey = 'RecordsTable_';
const initialExportType = first(EXPORT_OPTIONS).value;

/**
 * Table integrated with *RecordsLimiter* for reporting purpose.
 * @deprecated import RecordsTable from uidesktop instead
 */
export default class RecordsTable extends Component {
  constructor(props) {
    super(props);

    const key = prefixKey + initialExportType;
    this.state = {
      key,
      pageSize: this.props.pageSize || 100,
      defaultPageSize: this.props.defaultPageSize || 100,
      showExportModal: false,
      pageSizeOptions: this.props.pageSizeOptions || [5, 10, 20, 25, 50, 100],
      exportType: initialExportType,
    };
  }

  toggleExportModal = () => {
    const { showExportModal } = this.state;
    this.setState({
      showExportModal: !showExportModal,
      exportType: initialExportType,
    });
  };

  clickExportModal = () => {
    this.toggleExportModal();
    this.trackClickExport();
  };

  trackSelectRecord(recordSize) {
    const { sourcePage } = this.props;
    AnalyticsManager.instance().track(AnalyticsEvent.select_drop_down_records, {
      source_page: sourcePage,
      lang: getLanguage(),
      record: recordSize,
    });
  }

  trackSelectExport(fileType) {
    const { sourcePage } = this.props;
    AnalyticsManager.instance().track(AnalyticsEvent.choose_file_export, {
      source_page: sourcePage,
      lang: getLanguage(),
      file: fileType,
    });
  }

  trackClickExport() {
    const { sourcePage } = this.props;
    AnalyticsManager.instance().track(AnalyticsEvent.click_export, {
      source_page: sourcePage,
      lang: getLanguage(),
    });
  }

  renderBody = () => {
    const {
      emptyTitle,
      emptyDetail,
      loading,
      data,
      isTopScrollbar,
      columns,
      totalPages,
      manual,
      enableRows, // set as true to enable highlight on hover and onRowClick
      defaultSorted,
      onFetchData,
      onRowClick,
      disablePagination,
      disableRecordLimiter,
      isOverflowVisible,
      isValidation,
      isBulk,
      isValidateDataAndNameMatching,
      enableBottomRowRecords,
    } = this.props;
    const { key, pageSize } = this.state;

    if (data.length === 0) {
      if (loading)
        return <Loader title={getTranslation('common_pleaseWait')} />;
      return <EmptyState title={emptyTitle} detail={emptyDetail} />;
    }

    const tableCustomComponent = ({ children }) => {
      if (!isTopScrollbar)
        return isBulk ? (
          <div className={styles.scrollableFixedHeight}>{children}</div>
        ) : (
          <div className={styles.scrollable}>{children}</div>
        );
      return (
        <DoubleScrollbar totalData={data.length}>{children}</DoubleScrollbar>
      );
    };

    const columnHeadStyle = () => {
      return isValidation
        ? {
            style: {
              padding: 'var(--token-spacing-xs) var(--token-spacing-xxs)',
            },
          }
        : {
            style: {
              padding: 'var(--token-spacing-xs) var(--token-spacing-m)',
            },
          };
    };

    const columnStyle = () => {
      return isOverflowVisible
        ? {
            style: {
              padding: 'var(--token-spacing-xs) var(--token-spacing-m)',
              overflow: 'visible',
            },
          }
        : {
            style: {
              padding: 'var(--token-spacing-xs) var(--token-spacing-m)',
            },
          };
    };

    const tdCustomComponent = ({ className, style, children }) => {
      return (
        <td className={classNames('rt-td', className)} style={style}>
          <div
            onClick={e => {
              e.stopPropagation();
            }}
          >
            {children}
          </div>
        </td>
      );
    };

    const rowGroupStyle = (state, rowInfo) => {
      const isClickEnabled = typeof onRowClick === 'function';
      return {
        onClick: () => {
          if (isClickEnabled) {
            const data = rowInfo.original;
            onRowClick(data);
          }
        },
        style: {
          cursor: isClickEnabled ? 'pointer' : 'cursor',
        },
        className: styles.row,
      };
    };

    const enabledRowProps = {
      TdComponent: tdCustomComponent,
      getTrProps: rowGroupStyle,
    };

    const validationGroupStyle = (state, rowInfo) => {
      return {
        onClick: () => {
          if (isClickEnabled) {
            const data = rowInfo.original;
            onRowClick(data);
          }
        },
        style: {
          backgroundColor: isValidateDataAndNameMatching
            ? rowInfo.original.success === false
              ? 'var(--token-color-red10)'
              : rowInfo.original.match === false
              ? 'var(--token-color-yellow10)'
              : 'var(--token-color-white)'
            : rowInfo.original.success === false
            ? 'var(--token-color-red10)'
            : 'var(--token-color-white)',
        },
        className: styles.row,
      };
    };

    const isValidationProps = {
      getTdProps: validationGroupStyle,
    };

    if (enableBottomRowRecords) {
      return (
        <ReactTable
          previousText={
            <SystemIcon
              name="double-arrow-left"
              color={Colors.BLACK100}
              size={20}
            />
          }
          nextText={
            <SystemIcon
              name="double-arrow-right"
              color={Colors.BLACK100}
              size={20}
            />
          }
          getTheadProps={() => ({
            style: { zIndex: 1 },
          })}
          loadingText={<Loader title={getTranslation('common_pleaseWait')} />}
          key={key}
          columns={columns}
          loading={loading}
          data={data}
          showPageSizeOptions={true}
          defaultPageSize={this.state.defaultPageSize}
          pageSizeOptions={this.state.pageSizeOptions}
          pages={totalPages}
          showPagination={!disablePagination}
          disableRecordLimiter={disableRecordLimiter}
          manual={manual}
          onFetchData={onFetchData}
          minRows={0}
          TableComponent={tableCustomComponent}
          defaultSorted={defaultSorted}
          style={data.length > 10 ? { height: '640px' } : { minHeight: '100%' }}
          getTdProps={columnStyle}
          getTheadThProps={columnHeadStyle}
          {...(enableRows ? enabledRowProps : undefined)}
          {...(isValidation ? isValidationProps : undefined)}
        />
      );
    }

    return (
      <ReactTable
        previousText={
          <SystemIcon
            name="double-arrow-left"
            color={Colors.BLACK100}
            size={20}
          />
        }
        nextText={
          <SystemIcon
            name="double-arrow-right"
            color={Colors.BLACK100}
            size={20}
          />
        }
        getTheadProps={() => ({
          style: { zIndex: 1 },
        })}
        loadingText={<Loader title={getTranslation('common_pleaseWait')} />}
        key={key}
        columns={columns}
        loading={loading}
        data={data}
        showPageSizeOptions={false}
        defaultPageSize={this.state.defaultPageSize}
        pageSizeOptions={this.state.pageSizeOptions}
        pageSize={disablePagination ? data.length : pageSize}
        pages={totalPages}
        showPagination={!disablePagination}
        disableRecordLimiter={disableRecordLimiter}
        manual={manual}
        onFetchData={onFetchData}
        minRows={0}
        TableComponent={tableCustomComponent}
        defaultSorted={defaultSorted}
        style={data.length > 10 ? { height: '640px' } : { minHeight: '100%' }}
        getTdProps={columnStyle}
        getTheadThProps={columnHeadStyle}
        {...(enableRows ? enabledRowProps : undefined)}
        {...(isValidation ? isValidationProps : undefined)}
      />
    );
  };

  render() {
    const {
      title,
      loading,
      loadingExport,
      data,
      rightButtons,
      leftButtons,
      customLeftButtons,
      onFilterClick,
      idFilterName = 'btnFilter',
      idExportName = 'btnExport',
      onExportReport,
      additionalButtonForExportModal,
      disablePagination,
      disableRecordLimiter,
      customActionButtons,
      from = '',
      titleNotify = '',
      contentNotify = [],
      isUseNewTableHeader,
      tableSubheader,
      enableBottomRowRecords,
    } = this.props;
    const { exportType, showExportModal, pageSize } = this.state;
    const disabled = data.length === 0 || loading || loadingExport;

    const handlerOnRecordLimiterSubmit = e => {
      this.setState({
        key: prefixKey + e.value,
        pageSize: parseInt(e.value),
      });
      this.trackSelectRecord(e.value);
    };

    return (
      <div className="row">
        {isUseNewTableHeader && (
          <RecordsTableHeader
            clickExportModal={this.clickExportModal}
            disabled={disabled}
            pageSize={pageSize}
            handlerOnRecordLimiterSubmit={handlerOnRecordLimiterSubmit}
            {...this.props}
          />
        )}

        {!isUseNewTableHeader && (
          <div className="col-12 mb-3 d-flex align-items-center">
            {!title ? (
              customLeftButtons && (
                <div className="ml-3">{customLeftButtons}</div>
              )
            ) : (
              <h5 className="mb-0">
                <strong className="fs-16">{title}</strong>
              </h5>
            )}
            <div
              style={{ zIndex: 3 }}
              className="d-flex justify-content-end ml-auto"
            >
              {!disablePagination &&
                !disableRecordLimiter &&
                !enableBottomRowRecords && (
                  <RecordsLimiter
                    disabled={disabled}
                    value={{ value: pageSize, label: pageSize }}
                    onChange={handlerOnRecordLimiterSubmit}
                  />
                )}
              {leftButtons && <div className="ml-3">{leftButtons}</div>}
              {onFilterClick && (
                <div className="ml-3">
                  <ButtonWithIcon
                    id={idFilterName}
                    buttonType={ButtonWithIcon.Type.Outlined}
                    iconName="filter"
                    onClick={onFilterClick}
                  >
                    {getTranslation('common_filter')}
                  </ButtonWithIcon>
                </div>
              )}
              {onExportReport && (
                <div className="ml-3">
                  <ButtonWithIcon
                    id={idExportName}
                    disabled={disabled}
                    iconName="download"
                    onClick={this.clickExportModal}
                  >
                    {getTranslation('recordsTable_button_export')}
                  </ButtonWithIcon>
                </div>
              )}
              {rightButtons && <div className="ml-3">{rightButtons}</div>}
              {customActionButtons && (
                <div className={styles.customButtonContainer}>
                  <div className={styles.separator} />
                  <div className={styles.customActionButtons}>
                    {customActionButtons}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tableSubheader && <div className="col-12">{tableSubheader}</div>}

        <div className="col-12">
          <div className={styles.topScrollbarCard}>{this.renderBody()}</div>
        </div>
        <ExportReportsModal
          contentNotify={contentNotify}
          extraButtonExportModal={additionalButtonForExportModal}
          show={showExportModal}
          onClose={this.toggleExportModal}
          title={title}
          titleNotify={titleNotify}
          from={from}
          onChangeFileType={value => {
            this.setState({
              exportType: value,
            });
            this.trackSelectExport(value);
          }}
          onClickExport={() => {
            onExportReport(data, exportType);
            this.toggleExportModal();
          }}
          exportType={exportType}
        />
      </div>
    );
  }
}

RecordsTable.propTypes = {
  /** The table title */
  title: PropTypes.string,
  /** The table empty title */
  emptyTitle: PropTypes.string,
  /** To check whether loading state is visible or not */
  loading: PropTypes.bool,
  /** The data array that you want to display on the table */
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.array,
  /** The columns configuration object for the entire table */
  // eslint-disable-next-line react/forbid-prop-types
  columns: PropTypes.array,
  /** The total pages. Set this if you do pagination manually */
  totalPages: PropTypes.number,
  /** If you do pagination manually. You need to set `totalPages` and `onFetchData` option as well */
  manual: PropTypes.bool,
  /** If you want to enable row highlighting on hover and onClick behavior */
  enableRows: PropTypes.bool,
  /** The default sorted columns */
  defaultSorted: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      desc: PropTypes.bool,
    }),
  ),
  /** The additional action buttons on the rightmost side */
  rightButtons: PropTypes.element,
  /** The additional action buttons on the leftmost side */
  leftButtons: PropTypes.element,
  customLeftButtons: PropTypes.element,
  /** The additional action buttons on export modal */
  additionalButtonForExportModal: PropTypes.element,
  /** Gets called when the user clicks on the filter button */
  onFilterClick: PropTypes.func,
  /** Gets called when the user clicks on the export button */
  onExportReport: PropTypes.func,
  /** Gets called when the user clicks on the pagination button */
  onFetchData: PropTypes.func,
  /** Gets called when the user clicks on a row if enableRows is true. Optional. */
  onRowClick: PropTypes.func,
  /** Gets called when the user clicks on the export button */
  /** The total record per pages. Set this if you do pagination manually */
  pageSize: PropTypes.number,
  /** If you want to enable use new header */
  isUseNewTableHeader: PropTypes.bool,
  /** The additional table subheader below table header */
  tableSubheader: PropTypes.element,
  /** Gets called when the user type on search input. Not used if use old header. */
  onSearchInputChange: PropTypes.func,
  /** Gets called when the user click outside of search input. Not used if use old header. */
  onSearchInputBlur: PropTypes.func,
  /** If you want to enable row records setting on the bottom of the table */
  enableBottomRowRecords: PropTypes.bool,
};
